import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Plus, Calendar as CalendarIcon, Clock, Edit2, Trash2,
  MapPin, Navigation, Bell, AlignLeft, Sun, Repeat, AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../../contexts/CalendarContext';
import CreateEventModal from './CreateEventModal';
import DeleteModal from '../Common/DeleteModal';

const fmtTime = (time) => (time ? time.slice(0, 5) : '');

/* ── Tekrarlı Etkinlik Silme Modal'ı ─────────────────────────────── */
const RepeatDeleteModal = ({ isOpen, onClose, onDeleteOne, onDeleteAll, eventTitle, groupCount }) => {
  const { t } = useTranslation('calendar');
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 110000, backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ type: 'spring', damping: 28, stiffness: 380 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#0d1424] border border-white/[0.08] rounded-2xl w-full max-w-sm shadow-[0_24px_64px_rgba(0,0,0,0.7)]"
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Repeat className="w-4 h-4 text-amber-400" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[15px] font-bold text-[#dde8f5] leading-snug">{t('dayEventsModal.repeatDelete.title')}</h3>
                  <p className="text-xs text-[#4d6a8a] mt-1 leading-relaxed">
                    {t('dayEventsModal.repeatDelete.descriptionSingle')}{groupCount > 1 ? t('dayEventsModal.repeatDelete.descriptionCount', { count: groupCount }) : ''}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 text-[#4d6a8a] hover:text-[#8aaccc] hover:bg-white/[0.05] rounded-lg transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="h-px bg-white/[0.07]" />

            {/* Seçenekler */}
            <div className="p-4 space-y-2.5">
              {/* Sadece bu etkinliği sil */}
              <button
                onClick={onDeleteOne}
                className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-[#161f31] border border-white/[0.07] hover:bg-[#1a2640] hover:border-white/[0.12] transition-all text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#1e2d47] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#243650] transition-colors">
                  <Trash2 className="w-3.5 h-3.5 text-[#4d6a8a] group-hover:text-[#8aaccc] transition-colors" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#c8d8eb]">{t('dayEventsModal.repeatDelete.deleteOne')}</p>
                  <p className="text-xs text-[#4d6a8a] mt-0.5">{t('dayEventsModal.repeatDelete.deleteOneHint')}</p>
                </div>
              </button>

              {/* Tüm tekrarlananları sil */}
              <button
                onClick={onDeleteAll}
                className="w-full flex items-start gap-3 p-3.5 rounded-xl bg-red-500/[0.06] border border-red-500/[0.15] hover:bg-red-500/[0.12] hover:border-red-500/[0.25] transition-all text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-red-500/20 transition-colors">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-300">{t('dayEventsModal.repeatDelete.deleteAll')}</p>
                  <p className="text-xs text-red-400/70 mt-0.5">
                    {t('dayEventsModal.repeatDelete.deleteAllHint')}
                  </p>
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="px-4 pb-4">
              <button
                onClick={onClose}
                className="w-full py-2 text-sm text-[#4d6a8a] hover:text-[#8aaccc] hover:bg-white/[0.04] rounded-xl transition-colors"
              >
                {t('dayEventsModal.repeatDelete.cancel')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

/* ── Tek Etkinlik Kartı ───────────────────────────────────────────── */
const EventCard = ({ event, calendarColor, onEdit, onDelete }) => {
  const { t } = useTranslation('calendar');
  const mapsUrl = event.locationLat && event.locationLon
    ? `https://www.google.com/maps/dir/?api=1&destination=${event.locationLat},${event.locationLon}`
    : event.location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`
    : null;

  const reminderLabel =
    event.reminderDay && event.reminderDay !== '0'
      ? t('dayEventsModal.eventCard.reminderLabel', { days: event.reminderDay, time: event.reminderHour || '09:00' })
      : null;

  return (
    <div className="group relative rounded-2xl border border-white/[0.07] bg-[var(--color-bg-muted)] overflow-hidden">
      {/* Sol renk şeridi — takvim rengi sadece bu 3px çizgi */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl"
        style={{ backgroundColor: calendarColor || '#3d8ef0' }}
      />

      <div className="pl-5 pr-4 py-4">
        {/* Başlık + Aksiyon butonları */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-[var(--color-text-primary)] font-bold text-[15px] leading-snug">
                {event.title}
              </h4>
              {event.repeatGroupId && (
                <span title={t('dayEventsModal.eventCard.recurringTitle')}>
                  <Repeat className="w-3 h-3 text-[#4d6a8a] flex-shrink-0" strokeWidth={2} />
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg bg-white/[0.06] text-[#8aaccc] hover:text-[#3d8ef0] hover:bg-[#2563eb]/15 transition-colors"
              title={t('dayEventsModal.eventCard.edit')}
            >
              <Edit2 className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg bg-white/[0.06] text-[#8aaccc] hover:text-red-400 hover:bg-red-500/15 transition-colors"
              title={t('dayEventsModal.eventCard.delete')}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Saat */}
        <div className="mt-1.5">
          {event.allDay ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-muted)]">
              <Sun className="w-3.5 h-3.5" strokeWidth={2} />
              {t('dayEventsModal.eventCard.allDay')}
            </span>
          ) : event.startTime ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-[var(--color-text-secondary)]">
              <Clock className="w-3.5 h-3.5 text-[var(--color-text-muted)]" strokeWidth={2} />
              {fmtTime(event.startTime)}
              {event.endTime && (
                <span className="text-[var(--color-text-muted)] font-normal">→ {fmtTime(event.endTime)}</span>
              )}
            </span>
          ) : null}
        </div>

        {/* Açıklama */}
        {event.description && (
          <div className="mt-3 flex items-start gap-2">
            <AlignLeft className="w-3.5 h-3.5 text-[var(--color-text-muted)] mt-0.5 flex-shrink-0" strokeWidth={2} />
            <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed line-clamp-3">
              {event.description}
            </p>
          </div>
        )}

        {/* Konum */}
        {event.location && (
          <div className="mt-3 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-[var(--color-text-muted)] flex-shrink-0" strokeWidth={2} />
            <span className="text-xs text-[var(--color-text-secondary)] flex-1 truncate">{event.location}</span>
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ml-1 flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-[#1e2d47] text-[#3d8ef0] hover:bg-[#2563eb] hover:text-white border border-[#3d8ef0]/40 hover:border-[#3d8ef0] transition-colors"
              >
                <Navigation className="w-3 h-3" strokeWidth={2.5} />
                {t('dayEventsModal.eventCard.directions')}
              </a>
            )}
          </div>
        )}

        {/* Hatırlatma */}
        {reminderLabel && (
          <div className="mt-3 flex items-center gap-2">
            <Bell className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" strokeWidth={2} />
            <span className="text-xs text-amber-300/80 font-medium">{reminderLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Ana Modal ────────────────────────────────────────────────────── */
const DayEventsModal = ({ isOpen, onClose, selectedDate, calendar }) => {
  const { t, i18n } = useTranslation('calendar');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { getEventsForDate, deleteEvent, deleteEventGroup, createEvent, updateEvent, events: allEvents } = useCalendar();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState({ isOpen: false, eventId: null });
  const [showRepeatDeleteModal, setShowRepeatDeleteModal] = useState({ isOpen: false, event: null });
  const [editingEvent, setEditingEvent] = useState(null);

  const dayEvents = selectedDate && calendar ? getEventsForDate(selectedDate, calendar.id) : [];

  // Tekrarlı etkinlik grubundaki toplam sayıyı hesapla
  const getGroupCount = (repeatGroupId) => {
    if (!repeatGroupId) return 0;
    return allEvents.filter((e) => e.repeatGroupId === repeatGroupId).length;
  };

  const handleDeleteClick = (event, e) => {
    if (e) e.stopPropagation();
    if (event.repeatGroupId) {
      // Tekrarlı etkinlik: seçim modalını göster
      setShowRepeatDeleteModal({ isOpen: true, event });
    } else {
      // Tekrarsız etkinlik: normal silme onayı
      setShowDeleteModal({ isOpen: true, eventId: event.id });
    }
  };

  const handleDeleteConfirm = () => {
    if (showDeleteModal.eventId) deleteEvent(showDeleteModal.eventId);
    setShowDeleteModal({ isOpen: false, eventId: null });
  };

  // Tekrarlı etkinlik: sadece bunu sil
  const handleRepeatDeleteOne = () => {
    if (showRepeatDeleteModal.event) deleteEvent(showRepeatDeleteModal.event.id);
    setShowRepeatDeleteModal({ isOpen: false, event: null });
  };

  // Tekrarlı etkinlik: tüm grubu sil
  const handleRepeatDeleteAll = () => {
    if (showRepeatDeleteModal.event?.repeatGroupId) {
      deleteEventGroup(showRepeatDeleteModal.event.repeatGroupId);
    }
    setShowRepeatDeleteModal({ isOpen: false, event: null });
  };

  const handleEditClick = (event, e) => {
    if (e) e.stopPropagation();
    setEditingEvent(event);
    setShowCreateModal(true);
  };

  const handleCreateClick = () => {
    setEditingEvent(null);
    setShowCreateModal(true);
  };

  const handleCreateEvent = (eventData) => {
    if (editingEvent?.id) {
      updateEvent({ ...editingEvent, ...eventData });
      setEditingEvent(null);
    } else {
      createEvent(eventData);
    }
    setShowCreateModal(false);
  };

  if (!isOpen || !selectedDate) return null;

  const dayOfMonth = format(selectedDate, 'd', { locale: dateLocale });
  const monthYear = format(selectedDate, 'MMMM yyyy', { locale: dateLocale });
  const weekday = format(selectedDate, 'EEEE', { locale: dateLocale });
  const calColor = calendar?.color || '#3d8ef0';

  // CreateEventModal veya DeleteModal açıkken DayEventsModal gizlenir (z-index çakışması önlenir)
  const isSubModalOpen = showCreateModal || showDeleteModal.isOpen || showRepeatDeleteModal.isOpen;

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isSubModalOpen ? 0 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 flex items-center justify-center p-4 pointer-events-auto"
          style={{
            zIndex: isSubModalOpen ? 99000 : 100000,
            backgroundColor: isSubModalOpen ? 'transparent' : 'rgba(0,0,0,0.65)',
            backdropFilter: isSubModalOpen ? 'none' : 'blur(6px)',
            pointerEvents: isSubModalOpen ? 'none' : 'auto',
          }}
          onClick={isSubModalOpen ? undefined : onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 16 }}
            transition={{ type: 'spring', damping: 28, stiffness: 380 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[var(--color-bg-base)] rounded-3xl border border-white/[0.08] w-full max-w-md flex flex-col shadow-[0_32px_80px_rgba(0,0,0,0.7)]"
            style={{ maxHeight: 'min(86vh, 680px)' }}
          >
            {/* ── Header ── */}
            <div className="px-6 pt-6 pb-5 flex-shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  {/* Gün kutusu */}
                  <div className="w-14 h-14 rounded-2xl bg-[#1e2d47] border border-white/[0.08] flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-black text-[var(--color-text-primary)] leading-none">
                      {dayOfMonth}
                    </span>
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mt-0.5">
                      {format(selectedDate, 'MMM', { locale: dateLocale })}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-[var(--color-text-primary)] leading-tight capitalize">
                      {weekday}
                    </h3>
                    <p className="text-sm text-[var(--color-text-muted)] mt-0.5 capitalize">{monthYear}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span
                        className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: calColor }}
                      />
                      <span className="text-xs text-[var(--color-text-muted)] font-medium">
                        {dayEvents.length > 0 ? t('dayEventsModal.eventsCount', { count: dayEvents.length }) : t('dayEventsModal.noEvents')}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.06] rounded-xl transition-colors flex-shrink-0 mt-0.5"
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="h-px bg-white/[0.07] flex-shrink-0" />

            {/* ── Content ── */}
            <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
              {dayEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 text-center px-4">
                  <div className="w-16 h-16 rounded-2xl bg-[var(--color-bg-muted)] border border-white/[0.07] flex items-center justify-center mb-4">
                    <CalendarIcon className="w-7 h-7 text-[var(--color-text-muted)]" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-base font-bold text-[var(--color-text-primary)] mb-1.5">{t('dayEventsModal.noEventsForDayTitle')}</h4>
                  <p className="text-sm text-[var(--color-text-muted)] leading-relaxed mb-6">
                    {t('dayEventsModal.noEventsForDayHint')}
                  </p>
                  <button
                    onClick={handleCreateClick}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                    {t('dayEventsModal.createEvent')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {dayEvents.map((event, idx) => (
                    <EventCard
                      key={`${event.id}-${idx}`}
                      event={event}
                      calendarColor={calColor}
                      onEdit={(e) => handleEditClick(event, e)}
                      onDelete={(e) => handleDeleteClick(event, e)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            {dayEvents.length > 0 && (
              <>
                <div className="h-px bg-white/[0.07] flex-shrink-0" />
                <div className="px-4 py-3.5 flex-shrink-0">
                  <button
                    onClick={handleCreateClick}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                    {t('dayEventsModal.addNewEvent')}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const repeatEvent = showRepeatDeleteModal.event;
  const groupCount = repeatEvent ? getGroupCount(repeatEvent.repeatGroupId) : 0;

  return (
    <>
      {ReactDOM.createPortal(modal, document.body)}

      {/* Create/Edit — kendi z-index'i (100000) DayEventsModal'ın üstünde kalır */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setEditingEvent(null); }}
        onCreate={handleCreateEvent}
        onUpdate={handleCreateEvent}
        mode={editingEvent ? 'edit' : 'create'}
        initialEvent={editingEvent}
        selectedDate={selectedDate}
        calendar={calendar}
      />

      {/* Tekrarsız etkinlik silme onayı */}
      <DeleteModal
        isOpen={showDeleteModal.isOpen}
        onClose={() => setShowDeleteModal({ isOpen: false, eventId: null })}
        onConfirm={handleDeleteConfirm}
        title={t('dayEventsModal.deleteModal.title')}
        message={t('dayEventsModal.deleteModal.message')}
      />

      {/* Tekrarlı etkinlik silme seçimi */}
      <RepeatDeleteModal
        isOpen={showRepeatDeleteModal.isOpen}
        onClose={() => setShowRepeatDeleteModal({ isOpen: false, event: null })}
        onDeleteOne={handleRepeatDeleteOne}
        onDeleteAll={handleRepeatDeleteAll}
        eventTitle={repeatEvent?.title || ''}
        groupCount={groupCount}
      />
    </>
  );
};

export default DayEventsModal;
